import {faFilePlus, faTrash} from '@fortawesome/pro-solid-svg-icons';
import {Field, useField} from 'formik';
import {useState} from 'react';
import {useDropzone} from 'react-dropzone';
import styled from 'styled-components';

import {FieldMargin} from '../../_old/FieldHelpers/FieldContainers';
import {FormError} from '../../_old/FieldHelpers/FormError';
import {scToTwColour} from '../../theme';
import {IconButton} from '../Button/IconButton';

import {DropzoneLabel} from './common';
import Dropzone from './Dropzone';
import {getAcceptLabels, getAcceptTypes} from './functions';

const DEFAULT_DATA_ACCEPTED = 'image/jpeg';

// Formik + Yup want error schema to match values schema
// error strings must be at terminus properties, ie. MultiFileField['new'] not MultiFileField
// choosing new over count is mostly arbitrary
// actual field still works with count so it receives un-appended name
const getAppendedName = (n: string): string => n + '.new';

const handleEscKey = (e: React.KeyboardEvent): void => {
  e.key === 'Escape' && e.stopPropagation();
};

type MultiFileUploadInnerProps = React.HTMLAttributes<HTMLDivElement> & {
  name: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
};
function MultiFileUploadInner(
  props: MultiFileUploadInnerProps
): React.JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  const [{value}, , {setValue}] = useField<MultiFileField>(props.name);
  const [, , {setTouched, setError}] = useField<MultiFileField>(
    getAppendedName(props.name)
  );
  const remainingCount = value.count;
  const newCount = value.new.length;

  const accept = props.inputProps?.accept || DEFAULT_DATA_ACCEPTED;
  const [shortLabel, fullLabel] = getAcceptLabels(accept);

  const {getRootProps, getInputProps, open} = useDropzone({
    accept: getAcceptTypes(accept),
    disabled: props.inputProps?.disabled,
    onDropAccepted: (acceptedFiles) => {
      setValue({
        new: [...value.new, ...acceptedFiles],
        count: value.count,
      });
      setError('');
      setTouched(true, false);
    },
    onDropRejected: (rejects) => {
      const error = rejects[0].errors[0];
      const errorStr =
        error.code === 'file-invalid-type'
          ? `File types must be the following: ${fullLabel}`
          : error.message;
      setError(errorStr);
      setTouched(true, false);
      setIsHovered(false);
    },
    multiple: true,
    onDragEnter: () => setIsHovered(true),
    onDragLeave: () => setIsHovered(false),
  });

  const removeFiles = (isRemovingAll = false): void => {
    setValue({
      new: [],
      count: isRemovingAll ? 0 : value.count,
    });
  };

  const numOfThumbnails = remainingCount || newCount > 9 ? 6 : 9;

  // calculate number in '+' square
  const dynamicPlusCount =
    remainingCount +
    newCount -
    (numOfThumbnails -
      (newCount < numOfThumbnails ? numOfThumbnails - newCount : 0));

  return (
    <>
      <Dropzone
        $isHovered={isHovered}
        {...getRootProps({
          onKeyUp: handleEscKey,
          tabIndex: -1,
        })}
      >
        <input
          {...getInputProps({
            id: props.name,
          })}
        />

        {remainingCount + newCount > 0 ? (
          newCount > 0 ? (
            <Container>
              {value.new.slice(0, numOfThumbnails).map((file, i) => (
                <FilenameLabel key={i}>{file.name}</FilenameLabel>
              ))}

              {numOfThumbnails === 6 && <Count>{`+${dynamicPlusCount}`}</Count>}
            </Container>
          ) : (
            <Count>{remainingCount} previously uploaded.</Count>
          )
        ) : (
          <DropzoneLabel acceptLabels={shortLabel} isHovered={isHovered} />
        )}
      </Dropzone>

      <Controls
        filesCount={newCount + remainingCount}
        removeFiles={removeFiles}
        open={open}
      />
    </>
  );
}
const Container = styled.div`
  ${({theme}) => theme.media.xs`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr;
    grid-gap: 5px;
  `}
`;

const Count = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  color: ${scToTwColour('theme.colours.white')};
  font-size: 1.2rem;
  line-height: 1em;
  padding: 1em 0.5em;
  background-color: ${scToTwColour('theme.colours.blue9')};

  ${({theme}) => theme.media.xs`
    grid-row: 1 / -1;
    grid-column: 3 / 4;
  `}

  ${({theme}) => theme.media.md`
    font-size: 1.5rem;
  `}
`;
const FilenameLabel = styled.figcaption`
  font-size: 0.75rem;
  color: ${scToTwColour('theme.colours.white')};
  text-align: center;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  padding: 0.5em;
  background-color: ${scToTwColour('theme.colours.blue9')};
`;

type ControlsProps = {
  removeFiles: (b: boolean) => void;
  open: () => void;
  filesCount: number;
};
function Controls({
  removeFiles,
  open,
  filesCount,
}: ControlsProps): React.JSX.Element {
  const [isTrashExpanded, setIsTrashExpanded] = useState(false);

  const handleClick = (isRemovingAll: boolean): void => {
    setIsTrashExpanded(false);
    removeFiles(isRemovingAll);
  };

  return (
    <ButtonGroup>
      {!isTrashExpanded ? (
        <IconButton
          icon={faTrash}
          disabled={!filesCount}
          colour="tomato"
          onClick={() => setIsTrashExpanded(true)}
        />
      ) : (
        <TrashContainer>
          <button
            className="btn-danger"
            type="button"
            onClick={() => handleClick(true)}
          >
            All files
          </button>
          <button
            className="btn-danger"
            type="button"
            onClick={() => handleClick(false)}
          >
            New files
          </button>
        </TrashContainer>
      )}
      <IconButton icon={faFilePlus} onKeyUp={handleEscKey} onClick={open} />
    </ButtonGroup>
  );
}

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;
const TrashContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.6rem;
  margin-right: 0.5em;

  & > * {
    margin: 0 0 0 0.5em;
  }
`;

type MultiFileUploadProps = React.HTMLAttributes<HTMLDivElement> & {
  name: string;
  children: React.ReactNode;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
};
export function MultiFileUpload(
  props: MultiFileUploadProps
): React.JSX.Element {
  const {name, children, ...restProps} = props;
  const [, {error, touched}] = useField<MultiFileField>(getAppendedName(name));

  return (
    <FieldMargin>
      <Label
        className="small-upper"
        required={!!restProps.inputProps?.required}
      >
        {children}
      </Label>

      <Field name={name}>
        {() => <MultiFileUploadInner name={name} {...restProps} />}
      </Field>
      {error && touched && <FormError>{error}</FormError>}
    </FieldMargin>
  );
}
export type MultiFileField = {
  new: File[];
  count: number;
};

const Label = styled.p<{required: boolean}>`
  position: relative;
`;
